import React, { useState, useEffect } from 'react';
import { IoAdd, IoTrash, IoSchool, IoCalendar } from 'react-icons/io5';
import { School, SchoolCreateRequest, SchoolUpdateRequest, Course, SUBJECTS, LEVELS } from '@/services/educationService';
import { SchoolDirectory } from '@/services/schoolSearchService';
import SchoolSearch from '@/components/SchoolSearch';
import { useEducationData } from '@/hooks/useEducationData';
import './EducationForm.css';

interface EducationFormInlineProps {
  school?: School;
  onSuccess?: () => void;
  onCancel?: () => void;
}

interface CourseFormData {
  course_name: string;
  subject?: string;
  level?: string;
  subject_override?: string;
  level_override?: string;
  order_index?: number;
}

const EducationFormInline: React.FC<EducationFormInlineProps> = ({
  school,
  onSuccess,
  onCancel
}) => {
  const { createSchool, updateSchool, analyzeCourse, isCreatingSchool, isUpdatingSchool, isAnalyzingCourse } = useEducationData();
  
  // Form state
  const [selectedSchool, setSelectedSchool] = useState<SchoolDirectory | null>(null);
  const [formData, setFormData] = useState<SchoolCreateRequest>({
    school_directory_id: undefined,
    school_name: '',
    date_of_entry: '',
    date_of_exit: '',
    expect_to_graduate: true,
    is_boarding_school: false,
    graduated_early: false,
    graduated_late: false,
    took_time_off: false,
    took_gap_year: false,
    changed_schools: false,
    gpa_value: undefined,
    gpa_max: undefined,
    gpa_weighted: false,
    class_rank: '',
    received_counselor_help: false,
    has_academic_honors: false,
    courses: []
  });

  const [courses, setCourses] = useState<CourseFormData[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [currentStep, setCurrentStep] = useState(1);
  
  // Additional high schools state
  const [additionalSchoolsCount, setAdditionalSchoolsCount] = useState(0);
  const [additionalSchools, setAdditionalSchools] = useState<Array<{
    school: SchoolDirectory | null;
    school_name: string;
    date_of_entry: string;
    date_of_exit: string;
  }>>([]);

  // College coursework state
  const [takenCollegeCoursework, setTakenCollegeCoursework] = useState(false);
  const [collegeCourseworkCount, setCollegeCourseworkCount] = useState(0);
  const [collegeCoursework, setCollegeCoursework] = useState<Array<{
    college_name: string;
    start_date: string;
    end_date: string;
    program_type: string;
    highest_degree: string;
  }>>([]);

  // Initialize form data when school prop changes
  useEffect(() => {
    if (school) {
      setFormData({
        school_directory_id: school.school_directory_id,
        school_name: school.school_name,
        date_of_entry: school.date_of_entry || '',
        date_of_exit: school.date_of_exit || '',
        expect_to_graduate: school.expect_to_graduate,
        is_boarding_school: school.is_boarding_school,
        graduated_early: school.graduated_early,
        graduated_late: school.graduated_late,
        took_time_off: school.took_time_off,
        took_gap_year: school.took_gap_year,
        changed_schools: school.changed_schools,
        gpa_value: school.gpa_value,
        gpa_max: school.gpa_max,
        gpa_weighted: school.gpa_weighted,
        class_rank: school.class_rank || '',
        received_counselor_help: school.received_counselor_help,
        has_academic_honors: school.has_academic_honors,
        courses: []
      });
      
      setCourses(school.courses?.map(course => ({
        course_name: course.course_name,
        subject: course.subject,
        level: course.level,
        subject_override: course.subject_override,
        level_override: course.level_override
      })) || []);

      // Initialize additional schools if they exist
      if (school.changed_schools && (school as any).additional_schools) {
        const additionalSchoolsData = (school as any).additional_schools;
        setAdditionalSchoolsCount(additionalSchoolsData.length);
        setAdditionalSchools(additionalSchoolsData);
      }

      // Initialize college coursework if it exists
      if ((school as any).taken_college_coursework) {
        setTakenCollegeCoursework(true);
        const collegeCourseworkData = (school as any).college_coursework || [];
        setCollegeCourseworkCount(collegeCourseworkData.length);
        setCollegeCoursework(collegeCourseworkData);
      }
    } else {
      // Reset form for new school
      setSelectedSchool(null);
      setFormData({
        school_directory_id: undefined,
        school_name: '',
        date_of_entry: '',
        date_of_exit: '',
        expect_to_graduate: true,
        is_boarding_school: false,
        graduated_early: false,
        graduated_late: false,
        took_time_off: false,
        took_gap_year: false,
        changed_schools: false,
        gpa_value: undefined,
        gpa_max: undefined,
        gpa_weighted: false,
        class_rank: '',
        received_counselor_help: false,
        has_academic_honors: false,
        courses: []
      });
      setCourses([]);
      setAdditionalSchoolsCount(0);
      setAdditionalSchools([]);
      setTakenCollegeCoursework(false);
      setCollegeCourseworkCount(0);
      setCollegeCoursework([]);
    }
    setCurrentStep(1);
    setErrors({});
  }, [school]);

  const handleInputChange = (field: keyof SchoolCreateRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Handle changed_schools checkbox
    if (field === 'changed_schools') {
      if (!value) {
        // If unchecked, reset additional schools
        setAdditionalSchoolsCount(0);
        setAdditionalSchools([]);
      }
    }
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSchoolSelect = (school: SchoolDirectory | null) => {
    setSelectedSchool(school);
    if (school) {
      setFormData(prev => ({
        ...prev,
        school_directory_id: school.id,
        school_name: school.name
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        school_directory_id: undefined,
        school_name: ''
      }));
    }
    // Clear school name error when school is selected
    if (errors.school_name) {
      setErrors(prev => ({ ...prev, school_name: '' }));
    }
  };

  const handleCourseChange = (index: number, field: keyof CourseFormData, value: string) => {
    setCourses(prev => prev.map((course, i) => 
      i === index ? { ...course, [field]: value } : course
    ));
  };

  const addCourse = () => {
    setCourses(prev => [...prev, { course_name: '' }]);
  };

  const removeCourse = (index: number) => {
    setCourses(prev => prev.filter((_, i) => i !== index));
  };

  const analyzeCourseHandler = async (index: number) => {
    const course = courses[index];
    if (!course.course_name.trim()) return;

    try {
      const analysis = await analyzeCourse(course.course_name);
      setCourses(prev => prev.map((c, i) => 
        i === index ? {
          ...c,
          subject: analysis.subject,
          level: analysis.level
        } : c
      ));
    } catch (error) {
      console.error('Failed to analyze course:', error);
    }
  };

  // Additional schools handlers
  const handleAdditionalSchoolsCountChange = (count: number) => {
    setAdditionalSchoolsCount(count);
    const newSchools = Array.from({ length: count }, (_, i) => 
      additionalSchools[i] || {
        school: null,
        school_name: '',
        date_of_entry: '',
        date_of_exit: ''
      }
    );
    setAdditionalSchools(newSchools);
  };

  const handleAdditionalSchoolChange = (index: number, field: string, value: any) => {
    setAdditionalSchools(prev => prev.map((school, i) => 
      i === index ? { ...school, [field]: value } : school
    ));
  };

  // College coursework handlers
  const handleTakenCollegeCourseworkChange = (value: boolean) => {
    setTakenCollegeCoursework(value);
    if (!value) {
      // If unchecked, reset college coursework
      setCollegeCourseworkCount(0);
      setCollegeCoursework([]);
    }
  };

  const handleCollegeCourseworkCountChange = (count: number) => {
    setCollegeCourseworkCount(count);
    const newCoursework = Array.from({ length: count }, (_, i) => 
      collegeCoursework[i] || {
        college_name: '',
        start_date: '',
        end_date: '',
        program_type: '',
        highest_degree: ''
      }
    );
    setCollegeCoursework(newCoursework);
  };

  const handleCollegeCourseworkChange = (index: number, field: string, value: string) => {
    setCollegeCoursework(prev => prev.map((coursework, i) => 
      i === index ? { ...coursework, [field]: value } : coursework
    ));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.school_name.trim()) {
      newErrors.school_name = 'School name is required';
    }

    if (formData.gpa_value !== undefined) {
      if (formData.gpa_value < 0 || formData.gpa_value > 10) {
        newErrors.gpa_value = 'GPA must be between 0 and 10';
      }
      if (formData.gpa_max !== undefined && formData.gpa_value > formData.gpa_max) {
        newErrors.gpa_value = 'GPA cannot be higher than maximum GPA';
      }
    }

    if (formData.gpa_max !== undefined && (formData.gpa_max < 0 || formData.gpa_max > 10)) {
      newErrors.gpa_max = 'Maximum GPA must be between 0 and 10';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      // Only include fields that are part of the SchoolCreate schema
      const submitData = {
        school_directory_id: formData.school_directory_id,
        school_name: formData.school_name,
        date_of_entry: formData.date_of_entry ? formData.date_of_entry : undefined,
        date_of_exit: formData.date_of_exit ? formData.date_of_exit : undefined,
        expect_to_graduate: formData.expect_to_graduate,
        is_boarding_school: formData.is_boarding_school,
        graduated_early: formData.graduated_early,
        graduated_late: formData.graduated_late,
        took_time_off: formData.took_time_off,
        took_gap_year: formData.took_gap_year,
        changed_schools: formData.changed_schools,
        gpa_value: formData.gpa_value,
        gpa_max: formData.gpa_max,
        gpa_weighted: formData.gpa_weighted,
        class_rank: formData.class_rank || undefined,
        received_counselor_help: formData.received_counselor_help,
        has_academic_honors: formData.has_academic_honors,
        courses: courses.filter(course => course.course_name.trim()).map(course => ({
          course_name: course.course_name,
          subject: course.subject || undefined,
          level: course.level || undefined,
          subject_override: course.subject_override || undefined,
          level_override: course.level_override || undefined,
          order_index: course.order_index || 0
        }))
      };

      if (school) {
        await updateSchool(school.id, submitData as SchoolUpdateRequest);
      } else {
        await createSchool(submitData);
      }
      
      onSuccess?.();
    } catch (error) {
      console.error('Failed to save school:', error);
    }
  };

  const nextStep = () => {
    if (currentStep === 1 && !formData.school_name.trim()) {
      setErrors({ school_name: 'School name is required to continue' });
      return;
    }
    setCurrentStep(prev => Math.min(prev + 1, 3));
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const isLoading = isCreatingSchool || isUpdatingSchool;

  return (
    <div className="education-form-inline">
      <div className="education-form-header">
        <div className="education-form-title">
          <IoSchool className="education-form-icon" />
          <h3>{school ? 'Edit School' : 'Add School'}</h3>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="education-form-steps">
        <div className={`education-step ${currentStep >= 1 ? 'active' : ''}`}>
          <span className="education-step-number">1</span>
          <span className="education-step-label">School Info</span>
        </div>
        <div className={`education-step ${currentStep >= 2 ? 'active' : ''}`}>
          <span className="education-step-number">2</span>
          <span className="education-step-label">Academic Info</span>
        </div>
        <div className={`education-step ${currentStep >= 3 ? 'active' : ''}`}>
          <span className="education-step-number">3</span>
          <span className="education-step-label">Courses</span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="education-form-content">
        {/* Step 1: School Information */}
        {currentStep === 1 && (
          <div className="education-form-step">
            <h4>School Information</h4>
            
            <div className="education-form-group">
              <label className="education-form-label">
                School Name *
              </label>
              <SchoolSearch
                value={selectedSchool}
                onChange={handleSchoolSelect}
                placeholder="Search for your school..."
                error={errors.school_name}
              />
              <span className="education-form-hint">
                Search for your school by name or location. We'll automatically fill in the details.
              </span>
            </div>

            {selectedSchool && (
              <div className="education-selected-school">
                <h5>Selected School</h5>
                <div className="education-school-details">
                  <div className="education-school-info">
                    <strong>{selectedSchool.name}</strong>
                    <span className="education-school-type">
                      {selectedSchool.school_type === 'private' ? 'Private' : 'Public'} School
                    </span>
                  </div>
                  {selectedSchool.address && (
                    <div className="education-school-address">
                      {selectedSchool.address}
                    </div>
                  )}
                  <div className="education-school-location">
                    {[selectedSchool.city, selectedSchool.state].filter(Boolean).join(', ')}
                  </div>
                  {selectedSchool.enrollment && (
                    <div className="education-school-enrollment">
                      Enrollment: {selectedSchool.enrollment.toLocaleString()} students
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="education-form-group">
              <label className="education-form-label">
                <input
                  type="checkbox"
                  checked={formData.is_boarding_school}
                  onChange={(e) => handleInputChange('is_boarding_school', e.target.checked)}
                />
                This is a boarding school
              </label>
            </div>

            <div className="education-form-row">
              <div className="education-form-group">
                <label className="education-form-label">
                  <IoCalendar className="education-label-icon" />
                  Date of Entry
                </label>
                <input
                  type="date"
                  className="education-form-input"
                  value={formData.date_of_entry}
                  onChange={(e) => handleInputChange('date_of_entry', e.target.value)}
                />
              </div>

              <div className="education-form-group">
                <label className="education-form-label">
                  <IoCalendar className="education-label-icon" />
                  Date of Exit
                </label>
                <input
                  type="date"
                  className="education-form-input"
                  value={formData.date_of_exit}
                  onChange={(e) => handleInputChange('date_of_exit', e.target.value)}
                />
              </div>
            </div>

            <div className="education-form-group">
              <label className="education-form-label">
                <input
                  type="checkbox"
                  checked={formData.expect_to_graduate}
                  onChange={(e) => handleInputChange('expect_to_graduate', e.target.checked)}
                />
                I expect to graduate from this school
              </label>
            </div>

            <div className="education-form-section">
              <h5>Graduation Status (check all that apply)</h5>
              <div className="education-checkbox-grid">
                <label className="education-form-label">
                  <input
                    type="checkbox"
                    checked={formData.graduated_early}
                    onChange={(e) => handleInputChange('graduated_early', e.target.checked)}
                  />
                  Graduated early
                </label>
                <label className="education-form-label">
                  <input
                    type="checkbox"
                    checked={formData.graduated_late}
                    onChange={(e) => handleInputChange('graduated_late', e.target.checked)}
                  />
                  Graduated late
                </label>
                <label className="education-form-label">
                  <input
                    type="checkbox"
                    checked={formData.took_time_off}
                    onChange={(e) => handleInputChange('took_time_off', e.target.checked)}
                  />
                  Took time off
                </label>
                <label className="education-form-label">
                  <input
                    type="checkbox"
                    checked={formData.took_gap_year}
                    onChange={(e) => handleInputChange('took_gap_year', e.target.checked)}
                  />
                  Took gap year
                </label>
                <label className="education-form-label">
                  <input
                    type="checkbox"
                    checked={formData.changed_schools}
                    onChange={(e) => handleInputChange('changed_schools', e.target.checked)}
                  />
                  Changed schools
                </label>
                <label className="education-form-label">
                  <input
                    type="checkbox"
                    checked={takenCollegeCoursework}
                    onChange={(e) => handleTakenCollegeCourseworkChange(e.target.checked)}
                  />
                  Taken coursework at a college or university
                </label>
              </div>
            </div>

            {/* Additional Schools Section - Show when "Changed schools" is checked */}
            {formData.changed_schools && (
              <div className="education-form-section">
                <h5>Additional High Schools</h5>
                <div className="education-form-group">
                  <label className="education-form-label">
                    How many other high schools did you attend?
                  </label>
                  <select
                    className="education-form-input"
                    value={additionalSchoolsCount}
                    onChange={(e) => handleAdditionalSchoolsCountChange(parseInt(e.target.value))}
                    style={{ maxWidth: '200px' }}
                  >
                    <option value={0}>Select number</option>
                    {[1, 2, 3, 4, 5].map(num => (
                      <option key={num} value={num}>{num}</option>
                    ))}
                  </select>
                </div>

                {/* Additional Schools List */}
                {additionalSchools.map((school, index) => (
                  <div key={index} className="education-form-section">
                    <h6 className="education-form-label">Additional School {index + 1}</h6>
                    
                    <div className="education-form-group">
                      <label className="education-form-label">School Name</label>
                      <SchoolSearch
                        value={school.school}
                        onChange={(selectedSchool) => handleAdditionalSchoolChange(index, 'school', selectedSchool)}
                        placeholder="Search for school..."
                      />
                    </div>

                    <div className="education-form-row">
                      <div className="education-form-group">
                        <label className="education-form-label">Entry Date</label>
                        <input
                          type="date"
                          className="education-form-input"
                          value={school.date_of_entry}
                          onChange={(e) => handleAdditionalSchoolChange(index, 'date_of_entry', e.target.value)}
                        />
                      </div>
                      <div className="education-form-group">
                        <label className="education-form-label">Exit Date</label>
                        <input
                          type="date"
                          className="education-form-input"
                          value={school.date_of_exit}
                          onChange={(e) => handleAdditionalSchoolChange(index, 'date_of_exit', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* College Coursework Section - Show when "Taken coursework at a college or university" is checked */}
            {takenCollegeCoursework && (
              <div className="education-form-section">
                <h5>College Coursework</h5>
                <div className="education-form-group">
                  <label className="education-form-label">
                    How many colleges or universities did you take coursework at?
                  </label>
                  <select
                    className="education-form-input"
                    value={collegeCourseworkCount}
                    onChange={(e) => handleCollegeCourseworkCountChange(parseInt(e.target.value))}
                    style={{ maxWidth: '200px' }}
                  >
                    <option value={0}>Select number</option>
                    {[1, 2, 3, 4, 5].map(num => (
                      <option key={num} value={num}>{num}</option>
                    ))}
                  </select>
                </div>

                {/* College Coursework List */}
                {collegeCoursework.map((coursework, index) => (
                  <div key={index} className="education-form-section">
                    <h6 className="education-form-label">College {index + 1}</h6>
                    
                    <div className="education-form-group">
                      <label className="education-form-label">College Name</label>
                      <input
                        type="text"
                        className="education-form-input"
                        value={coursework.college_name}
                        onChange={(e) => handleCollegeCourseworkChange(index, 'college_name', e.target.value)}
                        placeholder="Enter college or university name"
                      />
                    </div>

                    <div className="education-form-row">
                      <div className="education-form-group">
                        <label className="education-form-label">Start Date</label>
                        <input
                          type="date"
                          className="education-form-input"
                          value={coursework.start_date}
                          onChange={(e) => handleCollegeCourseworkChange(index, 'start_date', e.target.value)}
                        />
                      </div>
                      <div className="education-form-group">
                        <label className="education-form-label">End Date</label>
                        <input
                          type="date"
                          className="education-form-input"
                          value={coursework.end_date}
                          onChange={(e) => handleCollegeCourseworkChange(index, 'end_date', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="education-form-row">
                      <div className="education-form-group">
                        <label className="education-form-label">Program Type</label>
                        <select
                          className="education-form-input"
                          value={coursework.program_type}
                          onChange={(e) => handleCollegeCourseworkChange(index, 'program_type', e.target.value)}
                        >
                          <option value="">Select program type</option>
                          <option value="Dual Enrollment">Dual Enrollment</option>
                          <option value="Summer Program">Summer Program</option>
                          <option value="Direct Credit">Direct Credit</option>
                        </select>
                      </div>
                      <div className="education-form-group">
                        <label className="education-form-label">Highest Degree</label>
                        <select
                          className="education-form-input"
                          value={coursework.highest_degree}
                          onChange={(e) => handleCollegeCourseworkChange(index, 'highest_degree', e.target.value)}
                        >
                          <option value="">Select highest degree</option>
                          <option value="AA">AA (Associate of Arts)</option>
                          <option value="AS">AS (Associate of Science)</option>
                          <option value="BA">BA (Bachelor of Arts)</option>
                          <option value="BS">BS (Bachelor of Science)</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Academic Information */}
        {currentStep === 2 && (
          <div className="education-form-step">
            <h4>Academic Information</h4>
            
            <div className="education-form-section">
              <h5>GPA Information</h5>
              <div className="education-form-row">
                <div className="education-form-group">
                  <label className="education-form-label">GPA</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="10"
                    className={`education-form-input ${errors.gpa_value ? 'error' : ''}`}
                    value={formData.gpa_value || ''}
                    onChange={(e) => handleInputChange('gpa_value', e.target.value ? parseFloat(e.target.value) : undefined)}
                    placeholder="e.g., 3.75"
                  />
                  {errors.gpa_value && (
                    <span className="education-form-error">{errors.gpa_value}</span>
                  )}
                </div>

                <div className="education-form-group">
                  <label className="education-form-label">Max GPA</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="10"
                    className={`education-form-input ${errors.gpa_max ? 'error' : ''}`}
                    value={formData.gpa_max || ''}
                    onChange={(e) => handleInputChange('gpa_max', e.target.value ? parseFloat(e.target.value) : undefined)}
                    placeholder="e.g., 4.0"
                  />
                  {errors.gpa_max && (
                    <span className="education-form-error">{errors.gpa_max}</span>
                  )}
                </div>

                <div className="education-form-group">
                  <label className="education-form-label">
                    <input
                      type="checkbox"
                      checked={formData.gpa_weighted}
                      onChange={(e) => handleInputChange('gpa_weighted', e.target.checked)}
                    />
                    Weighted GPA
                  </label>
                </div>
              </div>
            </div>

            <div className="education-form-group">
              <label className="education-form-label">Class Rank</label>
              <input
                type="text"
                className="education-form-input"
                value={formData.class_rank}
                onChange={(e) => handleInputChange('class_rank', e.target.value)}
                placeholder="e.g., 8th in class, top 10%, top quartile"
              />
              <span className="education-form-hint">
                Examples: "8th in class", "top 10%", "top quartile". We'll automatically detect the type.
              </span>
            </div>

            <div className="education-form-section">
              <h5>Additional Information</h5>
              <div className="education-checkbox-grid">
                <label className="education-form-label">
                  <input
                    type="checkbox"
                    checked={formData.received_counselor_help}
                    onChange={(e) => handleInputChange('received_counselor_help', e.target.checked)}
                  />
                  I received help from a counselor outside my high school
                </label>
                <label className="education-form-label">
                  <input
                    type="checkbox"
                    checked={formData.has_academic_honors}
                    onChange={(e) => handleInputChange('has_academic_honors', e.target.checked)}
                  />
                  I've received academic honors in high school
                </label>
              </div>

              {/* Conditional Organization Name Input */}
              {formData.received_counselor_help && (
                <div className="education-form-group mt-4">
                  <label className="education-form-label">Organization Name</label>
                  <input
                    type="text"
                    className="education-form-input"
                    value={(formData as any).organization_name || ''}
                    onChange={(e) => handleInputChange('organization_name' as any, e.target.value)}
                    placeholder="Enter the name of the organization that provided counseling help"
                  />
                </div>
              )}

              {/* Conditional Academic Honors Inputs */}
              {formData.has_academic_honors && (
                <div className="education-form-section mt-4">
                  <h6 className="education-form-label">Academic Honors Details</h6>
                  <div className="education-form-row">
                    <div className="education-form-group">
                      <label className="education-form-label">Honor/Award Name</label>
                      <input
                        type="text"
                        className="education-form-input"
                        value={(formData as any).honor_name || ''}
                        onChange={(e) => handleInputChange('honor_name' as any, e.target.value)}
                        placeholder="e.g., National Honor Society, Dean's List"
                      />
                    </div>
                    <div className="education-form-group">
                      <label className="education-form-label">Year Received</label>
                      <input
                        type="number"
                        className="education-form-input"
                        value={(formData as any).honor_year || ''}
                        onChange={(e) => handleInputChange('honor_year' as any, e.target.value)}
                        placeholder="e.g., 2024"
                        min="2000"
                        max="2030"
                      />
                    </div>
                  </div>
                  <div className="education-form-group">
                    <label className="education-form-label">Description (Optional)</label>
                    <textarea
                      className="education-form-input"
                      value={(formData as any).honor_description || ''}
                      onChange={(e) => handleInputChange('honor_description' as any, e.target.value)}
                      placeholder="Brief description of the honor or award"
                      rows={3}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Courses */}
        {currentStep === 3 && (
          <div className="education-form-step">
            <h4>Recent Courses</h4>
            <p className="education-form-description">
              Add your most recent courses. We'll automatically analyze the subject and level.
            </p>

            <div className="education-courses-list">
              {courses.map((course, index) => (
                <div key={index} className="education-course-form">
                  <div className="education-course-header">
                    <span className="education-course-number">Course {index + 1}</span>
                    <button
                      type="button"
                      className="education-remove-course"
                      onClick={() => removeCourse(index)}
                    >
                      <IoTrash />
                    </button>
                  </div>

                  <div className="education-form-group">
                    <label className="education-form-label">Course Name</label>
                    <div className="education-course-input-group">
                      <input
                        type="text"
                        className="education-form-input"
                        value={course.course_name}
                        onChange={(e) => handleCourseChange(index, 'course_name', e.target.value)}
                        placeholder="e.g., AP Biology, Honors English, Calculus BC"
                      />
                      <button
                        type="button"
                        className="education-analyze-button"
                        onClick={() => analyzeCourseHandler(index)}
                        disabled={!course.course_name.trim() || isAnalyzingCourse}
                      >
                        {isAnalyzingCourse ? 'Analyzing...' : 'Analyze'}
                      </button>
                    </div>
                  </div>

                  <div className="education-form-row">
                    <div className="education-form-group">
                      <label className="education-form-label">Subject</label>
                      <select
                        className="education-form-input"
                        value={course.subject_override || course.subject || ''}
                        onChange={(e) => handleCourseChange(index, 'subject_override', e.target.value)}
                      >
                        <option value="">Auto-detected</option>
                        {SUBJECTS.map(subject => (
                          <option key={subject} value={subject}>{subject}</option>
                        ))}
                      </select>
                      {course.subject && !course.subject_override && (
                        <span className="education-auto-detected">Auto: {course.subject}</span>
                      )}
                    </div>

                    <div className="education-form-group">
                      <label className="education-form-label">Level</label>
                      <select
                        className="education-form-input"
                        value={course.level_override || course.level || ''}
                        onChange={(e) => handleCourseChange(index, 'level_override', e.target.value)}
                      >
                        <option value="">Auto-detected</option>
                        {LEVELS.map(level => (
                          <option key={level} value={level}>{level}</option>
                        ))}
                      </select>
                      {course.level && !course.level_override && (
                        <span className="education-auto-detected">Auto: {course.level}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              <button
                type="button"
                className="education-add-course-button"
                onClick={addCourse}
              >
                <IoAdd />
                Add Course
              </button>
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="education-form-actions">
          <div className="education-form-nav">
            {currentStep > 1 && (
              <button
                type="button"
                className="education-form-button secondary"
                onClick={prevStep}
              >
                Previous
              </button>
            )}
            
            {currentStep < 3 ? (
              <button
                type="button"
                className="education-form-button primary"
                onClick={nextStep}
              >
                Next
              </button>
            ) : (
              <div className="education-form-final-actions">
                {onCancel && (
                  <button
                    type="button"
                    className="education-form-button secondary"
                    onClick={onCancel}
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  className="education-form-button primary"
                  disabled={isLoading}
                >
                  {isLoading ? 'Saving...' : (school ? 'Update School' : 'Add School')}
                </button>
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default EducationFormInline;